import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { t } from '@/translations';
import HeroSlideshow from '@/components/HeroSlideshow';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const { language } = useLanguage();
  const [heroImages, setHeroImages] = useState([
    {
      url: 'https://customer-assets-lqy194kg.emergentagent.net/job_remember-truly/artifacts/kizespuf_Albiana%20%26%20Juri-86-1.webp',
      alt: 'Matrimoni - Albiana & Juri'
    },
    {
      url: 'https://customer-assets-lqy194kg.emergentagent.net/job_remember-truly/artifacts/uuhobkop_Futura-33.webp',
      alt: 'Famiglia - Futura'
    },
    {
      url: 'https://customer-assets-lqy194kg.emergentagent.net/job_remember-truly/artifacts/jjdldwn3_Pre-Wedding.%20Elena%20%26%20Luca-138.webp',
      alt: 'Pre-Wedding - Elena & Luca'
    },
    {
      url: 'https://customer-assets-lqy194kg.emergentagent.net/job_remember-truly/artifacts/0e0uyobs_Gaia-098.webp',
      alt: 'Ritratti - Gaia'
    },
    {
      url: 'https://customer-assets-lqy194kg.emergentagent.net/job_remember-truly/artifacts/8wjoz4pu_Cabo-19.webp',
      alt: 'Eventi - Cabo'
    }
  ]);

  useEffect(() => {
    // fetchFeaturedImages(); // Commentato - usiamo le foto hardcoded selezionate dall'utente
  }, []);

  // Funzione commentata - ora usiamo le foto selezionate manualmente
  // const fetchFeaturedImages = async () => {
  //   try {
  //     const response = await axios.get(`${API}/gallery`);
  //     const featured = response.data.filter(img => img.is_featured);
  //     if (featured.length > 0) {
  //       setHeroImages(featured.map(img => ({
  //         url: img.image_url,
  //         alt: img.alt_text || 'Featured image'
  //       })));
  //     }
  //   } catch (error) {
  //     console.error('Error fetching featured images:', error);
  //   }
  // };

  const services = [
    {
      title: t('nav.weddings', language),
      description: language === 'it' ? 'Reportage autentico ed elegante del vostro giorno speciale' : 'Authentic and elegant reportage of your special day',
      link: '/matrimoni',
      image: 'https://customer-assets-lqy194kg.emergentagent.net/job_remember-truly/artifacts/it4ch3xl_Varie-4.webp'
    },
    {
      title: t('nav.family', language),
      description: language === 'it' ? 'Momenti intimi e naturali con la vostra famiglia o con il vostro partner.' : 'Intimate and natural moments with your family or partner.',
      link: '/famiglia',
      image: 'https://customer-assets-lqy194kg.emergentagent.net/job_remember-truly/artifacts/a1svdf1y_Futura-33.webp'
    },
    {
      title: t('nav.events', language),
      description: language === 'it' ? 'Professionalità e creatività per eventi e aziende' : 'Professionalism and creativity for events and companies',
      link: '/eventi',
      image: 'https://customer-assets-lqy194kg.emergentagent.net/job_remember-truly/artifacts/m63pre5w_wealth%20forum%20-111.webp'
    },
    {
      title: t('nav.portraits', language),
      description: language === 'it' ? 'Ritratti autentici ed eleganti per valorizzare la tua immagine personale o professionale' : 'Authentic and elegant portraits to enhance your personal or professional image',
      link: '/ritratti',
      image: 'https://customer-assets-lqy194kg.emergentagent.net/job_remember-truly/artifacts/cg3p117r_A1890A7D-E6AA-4226-94F7-78BFFC8C7CFADSC02459-Edit.webp'
    }
  ];

  return (
    <div data-testid="home-page">
      <HeroSlideshow
        images={heroImages}
        title={t('hero.title', language)}
        subtitle={t('hero.subtitle', language)}
      />

      {/* Intro Section */}
      <section className="max-w-4xl mx-auto px-6 py-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <p className="text-xs tracking-[0.2em] uppercase text-primary font-semibold mb-4">
            {language === 'it' ? 'Benvenuto' : 'Welcome'}
          </p>
          <h2
            className="text-4xl sm:text-5xl font-light tracking-tight leading-tight mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
            data-testid="intro-title"
          >
            {t('home.introTitle', language)}
          </h2>
          <p className="text-base leading-relaxed text-muted-foreground max-w-prose mx-auto">
            {t('home.introText', language)}
          </p>
        </motion.div>
      </section>

      {/* Vision Section */}
      <section className="bg-secondary/30 py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <img
                src="https://customer-assets-lqy194kg.emergentagent.net/job_remember-truly/artifacts/1v2llbam_Screenshot%202026-07-20%20alle%2022.12.00.png"
                alt="Matteo Giacometti"
                className="w-full h-auto object-cover rounded-sm"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-xs tracking-[0.2em] uppercase text-primary font-semibold mb-4">
                {language === 'it' ? 'Filosofia' : 'Philosophy'}
              </p>
              <h2
                className="text-4xl sm:text-5xl font-light tracking-tight leading-tight mb-6"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                {t('home.visionTitle', language)}
              </h2>
              <p className="text-base leading-relaxed text-muted-foreground">
                {t('home.visionText', language)}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <p className="text-xs tracking-[0.2em] uppercase text-primary font-semibold mb-4">
            {language === 'it' ? 'Cosa faccio' : 'What I do'}
          </p>
          <h2
            className="text-4xl sm:text-5xl font-light tracking-tight leading-tight"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {t('home.servicesTitle', language)}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.link}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2, duration: 0.8 }}
              data-testid={`service-card-${index}`}
            >
              <Link to={service.link} className="group block">
                <div className="relative overflow-hidden rounded-sm mb-4">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-80 object-cover image-hover"
                  />
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-all duration-500" />
                </div>
                <h3
                  className="text-2xl sm:text-3xl font-normal mb-2"
                  style={{ fontFamily: 'Cormorant Garamond, serif' }}
                >
                  {service.title}
                </h3>
                <p className="text-sm text-muted-foreground">{service.description}</p>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Home;
